"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { User, Settings, LogOut, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { NotificationsBell } from "@/components/notifications/NotificationsBell";
import { useAuth } from "@/lib/auth/AuthContext";

// =============================================================================
// Types
// =============================================================================

export interface HeaderProps {
  userType: "nurse" | "facility" | "admin";
  userName?: string;
  userEmail?: string;
}

// =============================================================================
// User Menu Dropdown Component
// =============================================================================

interface UserMenuProps {
  userName?: string;
  userEmail?: string;
  userType: "nurse" | "facility" | "admin";
}

function UserMenu({ userName, userEmail, userType }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();

  // Prefer explicitly-passed props (used by facility/admin layouts to show
  // org/admin name); fall back to the authenticated user's email so we never
  // ship "Jane Doe" placeholder text again.
  const displayName = userName ?? user?.email ?? "Account";
  const displayEmail = userEmail ?? user?.email ?? "";
  const showEmailRow = displayEmail.length > 0 && displayEmail !== displayName;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const profilePath = userType === "nurse" ? "/nurse/profile" : userType === "facility" ? "/facility/profile" : "/admin/settings";

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 transition-colors hover:bg-slate-50"
        aria-label="User menu"
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-white">
          <User className="h-4 w-4" />
        </div>
        <div className="hidden text-left md:block">
          <div className="text-sm font-medium text-slate-900">
            {displayName}
          </div>
          <div className="text-xs text-slate-500">
            {userType === "nurse" ? "Nurse" : userType === "facility" ? "Facility" : "Admin"}
          </div>
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-slate-400 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-56 rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 px-4 py-3">
            <div className="text-sm font-medium text-slate-900">
              {displayName}
            </div>
            {showEmailRow && (
              <div className="text-xs text-slate-500">
                {displayEmail}
              </div>
            )}
          </div>

          <div className="py-1">
            <Link
              href={profilePath}
              className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
              onClick={() => setIsOpen(false)}
            >
              <User className="h-4 w-4" />
              Profile
            </Link>
            <Link
              href="/settings"
              className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
              onClick={() => setIsOpen(false)}
            >
              <Settings className="h-4 w-4" />
              Settings
            </Link>
          </div>

          <div className="border-t border-slate-100 py-1">
            <button
              type="button"
              disabled={isLoggingOut}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
              onClick={async () => {
                setIsLoggingOut(true);
                setIsOpen(false);
                try {
                  await logout();
                } finally {
                  setIsLoggingOut(false);
                }
              }}
            >
              <LogOut className="h-4 w-4" />
              {isLoggingOut ? "Signing out…" : "Logout"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Header Component
// =============================================================================

export function Header({ userType, userName, userEmail }: HeaderProps) {
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
      <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link
          href={userType === "nurse" ? "/nurse/dashboard" : userType === "facility" ? "/facility/dashboard" : "/admin/dashboard"}
          className="flex items-center gap-2"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
            <span className="text-lg font-bold text-white">V</span>
          </div>
          <span className="hidden text-xl font-bold text-slate-900 sm:inline">
            Velocity
          </span>
        </Link>

        {/* Right side: Notifications + User Menu */}
        <div className="flex items-center gap-4">
          <NotificationsBell />
          <UserMenu
            userName={userName}
            userEmail={userEmail}
            userType={userType}
          />
        </div>
      </div>
    </header>
  );
}
